import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { DownloadIcon, CheckIcon } from '@heroicons/react/solid';
import { db } from '../../../../firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useAuth } from '../../../../hooks/useAuth';
import {
  buildProformaExportJSON,
  generateProformaFilename,
  downloadJSON,
  moduleHasData,
} from '../utils/proformaExportUtils';

/**
 * ExportProformaDialog — modal dialog for exporting clinician proforma data as JSON.
 *
 * This dialog allows clinicians to select which assessment modules to include in a
 * JSON export of their decisions and notes. Currently supports:
 * - Diagnostic Decision (DD)
 * - Post-Assessment Discussion (PAD)
 *
 * Clinical Interview and Semi-Structured Interview are shown as greyed-out placeholders
 * (not yet available for proforma export) to communicate the planned roadmap.
 *
 * The export is entirely client-side — it reads from the in-memory patientDataModel,
 * assembles a structured JSON payload, and triggers a browser download. No network
 * requests are made, ensuring instant response times and offline capability.
 *
 * VISIBILITY GATE: This dialog should only be rendered for Well Life Clinic users.
 * The gating logic lives in PatientInfoPanel where the button is rendered.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is currently visible
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {Object} props.patientDataModel - The patient's full data model from context
 * @param {string} props.patientId - The patient's Firestore document ID
 */
export default function ExportProformaDialog({ isOpen, onClose, patientDataModel, patientId }) {
  const { user, claims } = useAuth();

  // Module selection state — DD and PAD default to selected (if they have data)
  const [selectedModules, setSelectedModules] = useState({ dd: true, pad: true });

  // Determine which modules have exportable data. Modules without data will
  // have their checkboxes disabled to prevent exporting empty stubs.
  const moduleDataAvailability = useMemo(() => ({
    dd: moduleHasData(patientDataModel, 'dd'),
    pad: moduleHasData(patientDataModel, 'pad'),
  }), [patientDataModel]);

  // Reset selection state when the dialog opens, pre-selecting modules that have data
  useEffect(() => {
    if (isOpen) {
      setSelectedModules({
        dd: moduleDataAvailability.dd,
        pad: moduleDataAvailability.pad,
      });
    }
  }, [isOpen, moduleDataAvailability]);

  const handleModuleToggle = useCallback((moduleKey) => {
    setSelectedModules((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }));
  }, []);

  // At least one module must be selected to enable the download button
  const hasSelection = useMemo(
    () => Object.values(selectedModules).some(Boolean),
    [selectedModules]
  );

  const handleDownload = useCallback(() => {
    if (!hasSelection) return;

    const jsonPayload = buildProformaExportJSON(patientDataModel, selectedModules);
    const filename = generateProformaFilename(patientDataModel);
    downloadJSON(jsonPayload, filename);

    // Fire-and-forget audit trail write to the same exportAuditTrail array
    // used by the Export Form Data CF. Uses arrayUnion for atomic append
    // without needing to read-then-write the full array.
    if (patientId) {
      const exportId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const auditEntry = {
        exportId,
        timestamp: new Date().toISOString(),
        requestType: 'proforma_json_export',
        patientId,
        clinicianId: user?.uid || 'unknown',
        organisation: claims?.organisation || 'unknown',
        exportFormat: 'json',
        selectedModules: { ...selectedModules },
        status: 'completed',
      };

      const patientRef = doc(db, 'patients', patientId);
      updateDoc(patientRef, { exportAuditTrail: arrayUnion(auditEntry) })
        .catch((err) => console.error('Failed to log proforma export audit:', err));
    }

    onClose();
  }, [hasSelection, patientDataModel, selectedModules, onClose, patientId, user, claims]);

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
          <AlertDialog.Title className="text-xl font-semibold mb-4">
            Export Proforma JSON
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-500 mb-4">
            Download clinician decisions and notes from selected assessment modules as a structured JSON file.
          </AlertDialog.Description>

          {/* Module selection — uses the same styled button rows as Export Form Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Modules to Export
            </label>
            <div className="grid grid-cols-1 gap-3">
              {/* Diagnostic Decision */}
              <button
                type="button"
                onClick={() => moduleDataAvailability.dd && handleModuleToggle('dd')}
                disabled={!moduleDataAvailability.dd}
                className={`inline-flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedModules.dd && moduleDataAvailability.dd
                    ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                    : moduleDataAvailability.dd
                      ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                    selectedModules.dd && moduleDataAvailability.dd
                      ? 'bg-violet-600 border-violet-600'
                      : moduleDataAvailability.dd
                        ? 'border-gray-300 bg-white'
                        : 'border-gray-200 bg-gray-50'
                  }`}>
                    {selectedModules.dd && moduleDataAvailability.dd && (
                      <CheckIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span>Diagnostic Decision</span>
                </div>
                {!moduleDataAvailability.dd && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">
                    No data available
                  </span>
                )}
              </button>

              {/* Post-Assessment Discussion */}
              <button
                type="button"
                onClick={() => moduleDataAvailability.pad && handleModuleToggle('pad')}
                disabled={!moduleDataAvailability.pad}
                className={`inline-flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedModules.pad && moduleDataAvailability.pad
                    ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                    : moduleDataAvailability.pad
                      ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                    selectedModules.pad && moduleDataAvailability.pad
                      ? 'bg-violet-600 border-violet-600'
                      : moduleDataAvailability.pad
                        ? 'border-gray-300 bg-white'
                        : 'border-gray-200 bg-gray-50'
                  }`}>
                    {selectedModules.pad && moduleDataAvailability.pad && (
                      <CheckIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span>Post-Assessment Discussion</span>
                </div>
                {!moduleDataAvailability.pad && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">
                    No data available
                  </span>
                )}
              </button>

              {/* Clinical Interview — disabled placeholder */}
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed"
              >
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded border-2 mr-3 border-gray-200 bg-gray-50" />
                  <span>Clinical Interview</span>
                </div>
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">
                  Coming soon
                </span>
              </button>

              {/* Semi-Structured Interview — disabled placeholder */}
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed"
              >
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded border-2 mr-3 border-gray-200 bg-gray-50" />
                  <span>Semi-Structured Interview</span>
                </div>
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">
                  Coming soon
                </span>
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-6 flex justify-between">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>

            <button
              type="button"
              onClick={handleDownload}
              disabled={!hasSelection}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                hasSelection
                  ? 'bg-violet-600 hover:bg-violet-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                <DownloadIcon className="h-4 w-4 mr-2" />
                Download JSON
              </div>
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
